import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Store, MapPin, Package, Star, Search, ArrowLeft, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import PublicShell from "../components/PublicShell";
import { useAuth } from "../context/AuthContext";
import { SYRIAN_CITIES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIMIT = 24;
const initials = (name) => (name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("") || "إ";

const StoreCard = ({ s }) => (
  <Link to={`/store/${s.id}`} data-testid={`market-store-${s.id}`}
    className="group bg-white rounded-2xl border border-[#7AB2B2]/60 p-4 flex flex-col gap-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 rounded-xl bg-[#EBF4F6] border border-[#7AB2B2]/40 overflow-hidden shrink-0 flex items-center justify-center">
        {s.has_logo ? (
          <img src={`${API}/public/stores/${s.id}/logo`} alt="" className="w-full h-full object-contain" data-testid={`market-logo-${s.id}`} />
        ) : (
          <span className="text-[#09637E] font-extrabold text-lg" data-testid={`market-initials-${s.id}`}>{initials(s.store_name)}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-extrabold text-[#333333] text-base leading-snug truncate" data-testid={`market-name-${s.id}`}>{s.store_name}</h3>
        <div className="text-xs text-[#666666] flex items-center gap-1 mt-0.5" data-testid={`market-governorate-${s.id}`}><MapPin size={12} className="text-[#09637E]" /> المحافظة: {s.governorate || "—"}</div>
      </div>
    </div>
    <div className="flex items-center justify-between text-xs text-[#666666]">
      <span className="flex items-center gap-1" data-testid={`market-count-${s.id}`}><Package size={13} /> {s.product_count} منتجات</span>
      <span className="flex items-center gap-1" data-testid={`market-rating-${s.id}`}>
        <Star size={13} className={s.rating_count ? "text-amber-500 fill-amber-500" : "text-[#AAAAAA]"} />
        {s.rating_count ? <><span dir="ltr" className="font-bold text-[#333333]">{s.rating_avg.toFixed(1)}</span> ({s.rating_count})</> : "لا يوجد تقييم بعد"}
      </span>
    </div>
    {s.material_types?.length > 0 && (
      <div className="flex flex-wrap gap-1" data-testid={`market-materials-${s.id}`}>
        {s.material_types.slice(0, 4).map((m) => <span key={m} className="text-[11px] bg-[#EBF4F6] text-[#09637E] rounded-full px-2 py-0.5">{m}</span>)}
        {s.material_types.length > 4 && <span className="text-[11px] text-[#666666]">+{s.material_types.length - 4}</span>}
      </div>
    )}
    <span className="mt-auto h-10 rounded-lg bg-[#09637E] group-hover:bg-[#088395] text-white font-bold text-sm flex items-center justify-center gap-1" data-testid={`market-enter-${s.id}`}>
      <Store size={16} /> دخول المتجر <ArrowLeft size={14} />
    </span>
  </Link>
);

// Admin/Founder-only management control: copy the canonical public marketplace URL (no backend, no DB write).
const AdminShareAll = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/stores`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("تم نسخ رابط السوق العام"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("تعذّر النسخ، انسخ الرابط يدوياً"); }
  };
  return (
    <div className="mb-6" data-testid="admin-share-all">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="admin-share-all-btn"
        className="h-11 px-5 rounded-lg bg-white border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] font-bold text-sm flex items-center gap-2">
        <Share2 size={16} /> مشاركة كل المتاجر
      </button>
      {open && (
        <div className="mt-3 bg-white border border-[#7AB2B2] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-share-all-panel">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-[#666666] mb-1">الرابط العام لسوق إمداد (يمكن إرساله لأي شخص)</div>
            <div className="font-mono text-sm text-[#333333] bg-[#F7FBFC] rounded-lg px-3 py-2 break-all" dir="ltr" data-testid="admin-share-all-url">{url}</div>
          </div>
          <button type="button" onClick={copy} data-testid="admin-share-all-copy"
            className="h-11 px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center justify-center gap-2 shrink-0">
            {copied ? <Check size={16} /> : <Copy size={16} />} نسخ الرابط
          </button>
        </div>
      )}
    </div>
  );
};

export default function Marketplace() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [applied, setApplied] = useState({ q: "", city: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { document.title = "تصفّح المتاجر — إمداد"; }, []);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/stores`, { params: { page, limit: LIMIT, q: applied.q, city: applied.city } })
      .then((r) => { setItems((prev) => (page === 1 ? r.data.items : [...prev, ...r.data.items])); setTotal(r.data.total); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, applied]);

  const apply = (e) => { e?.preventDefault(); setPage(1); setApplied({ q: q.trim(), city }); };

  return (
    <PublicShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="marketplace-page">
        <section className="relative overflow-hidden rounded-3xl bg-[#09637E] text-white p-6 md:p-10 mb-6" data-testid="marketplace-header">
          <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-white/10" />
          <div className="relative space-y-2">
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-xs font-bold">
              <span className="w-5 h-5 rounded bg-white text-[#09637E] flex items-center justify-center font-extrabold text-[11px]">إ</span> منصة إمداد
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">تصفّح متاجر إمداد</h1>
            <p className="text-sm opacity-90">اختر متجراً لتصفّح منتجاته والطلب منه مباشرة عبر منصة إمداد.</p>
          </div>
        </section>

        {user?.role === "admin" && <AdminShareAll />}

        <form onSubmit={apply} className="flex flex-col sm:flex-row gap-2 mb-6" data-testid="marketplace-filters">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7AB2B2]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم المتجر" data-testid="marketplace-search"
              className="w-full h-11 rounded-lg border border-[#7AB2B2] bg-white pr-9 pl-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
          </div>
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="marketplace-governorate" className="h-11 rounded-lg border border-[#7AB2B2] bg-white px-3 text-sm sm:w-48">
            <option value="">كل المحافظات</option>
            {SYRIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="submit" className="h-11 px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm" data-testid="marketplace-apply">بحث</button>
        </form>

        <div className="text-sm text-[#666666] mb-3" data-testid="marketplace-total">{loading && page === 1 ? "جارٍ التحميل..." : `${total} متجر`}</div>
        {!loading && items.length === 0 ? (
          <div className="bg-white border border-[#7AB2B2] rounded-2xl p-12 text-center text-[#666666] font-bold" data-testid="marketplace-empty">لا توجد متاجر مطابقة</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5" data-testid="marketplace-grid">
            {items.map((s) => <StoreCard key={s.id} s={s} />)}
          </div>
        )}
        {items.length < total && (
          <div className="text-center mt-6">
            <button onClick={() => setPage((n) => n + 1)} data-testid="marketplace-load-more" className="h-11 px-6 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold hover:bg-white">عرض المزيد ({total - items.length})</button>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
